import React, { useState, useMemo, useRef, useEffect } from "react";
import { Input } from "./ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { ACUTE_CONDITIONS, ACUTE_GROUPS } from "../lib/acuteConditions";
import { Search, Stethoscope } from "lucide-react";

// Searchable Combobox for the Secondary Diagnosis field.
// Free-text is preserved — typing anything not in the list still works.
// Selecting a known acute condition sets the label AND triggers the
// downstream AcuteSuggestionPanel via onKnownConditionChange.
export default function SecondaryDiagnosisCombobox({ value, onChange, onKnownConditionChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const needle = (q || value || "").toLowerCase().trim();
    const groups = ACUTE_GROUPS.map((g) => ({
      group: g,
      // Tolerant match so hybrid categories like "Bacterial/Viral" appear under BOTH buckets.
      items: ACUTE_CONDITIONS.filter((c) => (c.group || "").split("/").includes(g)).filter((c) => {
        if (!needle) return true;
        return (
          c.label.toLowerCase().includes(needle) ||
          c.aliases.some((a) => a.toLowerCase().includes(needle))
        );
      }),
    })).filter((g) => g.items.length > 0);
    return groups;
  }, [q, value]);

  // Whenever the free-text value changes (typed OR selected), re-evaluate
  // whether it maps to a known acute condition and inform the parent.
  useEffect(() => {
    if (!onKnownConditionChange) return;
    const v = (value || "").toLowerCase().trim();
    if (!v) return onKnownConditionChange(null);
    const found = ACUTE_CONDITIONS.find((c) =>
      c.label.toLowerCase() === v ||
      c.aliases.some((a) => a.toLowerCase() === v)
    );
    onKnownConditionChange(found || null);
  }, [value, onKnownConditionChange]);

  const pick = (label) => {
    onChange(label);
    setQ("");
    setOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); setQ(e.target.value); }}
            onFocus={() => setOpen(true)}
            placeholder="Search acute condition or type free-text…"
            className="pr-8"
            data-testid="dx-secondary-combobox"
          />
          <Stethoscope size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-pink-500" />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[380px] p-0 max-h-[420px] overflow-hidden flex flex-col"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-2 border-b border-slate-200 shrink-0">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 225+ acute conditions…"
              className="h-8 pl-7 text-sm"
              data-testid="dx-secondary-search"
              autoFocus
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 && (
            <div className="p-4 text-center text-xs text-slate-500">
              No match. Free-text OK — keep typing.
            </div>
          )}
          {filtered.map((g) => (
            <div key={g.group} className="border-b border-slate-100 last:border-b-0">
              <div className="px-3 py-1.5 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-semibold sticky top-0">
                {g.group}
              </div>
              {g.items.map((c) => (
                <button
                  key={c.id}
                  onClick={() => pick(c.label)}
                  className="w-full px-3 py-1.5 text-left hover:bg-pink-50 text-xs text-slate-800"
                  data-testid={`dx-secondary-opt-${c.id}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
